import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

/**
 * Shared layout for the four static legal / info pages: Terms, Privacy,
 * Contact, About. Keeps chrome consistent (top nav, max-width, footer)
 * so each page file only has to think about its content.
 */
export default function LegalPageLayout({ title, subtitle, children }) {
  const location = useLocation();

  // React Router doesn't auto-scroll to `#hash` targets on navigation.
  // We look it up after mount so links like `/terms#challenge-rules`
  // land on the right section instead of the top of the page.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      // Small delay lets the page paint before scrolling.
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="legal-page-layout">
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors" data-testid="legal-home-link">
            <ArrowLeft size={16} />
            <span className="font-heading font-bold text-[#F5C542]">Golden Tips</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/login" className="text-[#A1A1AA] hover:text-white">Sign in</Link>
            <Link to="/signup" className="btn-primary !w-auto !px-4 !py-2 text-sm">Get started</Link>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">{title}</h1>
          {subtitle && (
            <p className="text-[#A1A1AA] mb-8">{subtitle}</p>
          )}
          <div className="prose-legal space-y-5 text-[#D4D4D8] leading-relaxed text-[0.95rem]">
            {children}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
